<template>
  <FormField
    :id="id"
    :class="wrapperClass"
    :icon="icon"
    :label="label"
    :label-inside="labelInside"
    :label-class="labelClass"
    :is-disabled="isDisabled"
    :is-field-focused="isFocused || modelValue.length > 0"
    :is-required="isRequired"
    :is-label-visible="isLabelVisible"
    :is-error="isError"
    :message="message"
    :message-fixed="messageFixed"
    :is-message-visible="isMessageVisible"
  >
    <input
      :id="id"
      ref="inputRef"
      v-bind="$attrs"
      :value="modelValue"
      :disabled="isDisabled"
      :placeholder="!labelInside ? placeholder : undefined"
      :type="type"
      :class="[
        'transition-colors duration-300 ease-out',
        labelInside ? 'pb-1 pt-4' : 'py-3',
        { 'pl-14': icon },
        inputClass
      ]"
      @input="updateValue"
      @focus="isFocused = true"
      @blur="isFocused = false"
    />
    <Transition name="fade">
      <button
        v-if="showClear && modelValue"
        type="button"
        aria-label="Počisti"
        class="absolute right-4 top-0 h-full"
        @click="clearValue"
      >
        <Icon class="pointer-events-none" name="custom:close" :size="24" />
      </button>
    </Transition>
    <slot />
  </FormField>
</template>

<script setup lang="ts">
import type { FormFieldProps } from './FormField.vue'

type FormInputProps = {
  inputClass?: string
  modelValue?: string
  placeholder?: string
  type?: 'text' | 'email' | 'hidden' | 'search' | 'number' | 'tel' | 'url'
  showClear?: boolean
} & Pick<
  FormFieldProps,
  | 'wrapperClass'
  | 'icon'
  | 'label'
  | 'labelClass'
  | 'labelInside'
  | 'inputLabelClass'
  | 'isLabelVisible'
  | 'isRequired'
  | 'isDisabled'
  | 'isError'
  | 'messageFixed'
  | 'message'
  | 'isMessageVisible'
>

defineOptions({
  inheritAttrs: false
})

withDefaults(defineProps<FormInputProps>(), {
  inputClass: `form-input`,
  icon: undefined,
  modelValue: '',
  placeholder: '',
  type: 'text',
  isLabelVisible: true,
  labelInside: false,
  showClear: false
})

const emit = defineEmits(['update:modelValue', 'clear'])

const updateValue = (event: Event) => {
  emit('update:modelValue', (event.target as HTMLInputElement).value)
}

const clearValue = () => {
  emit('update:modelValue', '')
  emit('clear')
}

const inputRef = ref<null | HTMLElement>(null)
const id = useId()
const isFocused = ref(false)
</script>
